




































































import { Component, Mixins, Prop, Ref, Watch } from 'vue-property-decorator';
import { Button, Checkbox, Input, Form, FormItem } from 'element-ui';
import Message from '@/components/channel/Message.vue';
import ChannelOverlay from '@/components/channel/ChannelOverlay.vue';

// mixins
import ScrollBottom from '@/mixins/scrollBottom';

// services
import { channelService } from '@/services/ChannelService';
import { signedUploadService } from '@/services/SignedUploadService';

// gql
import ChannelMessageFragment from '@/graphql/fragments/ChannelMessageFields.gql';
import fetchChannelsWithInquiryQuery from '@/graphql/queries/channels/FetchChannelsWithInquiry.gql';

// types
import {
  ChannelFields,
  ChannelMessageFields,
  ChannelTypeEnum,
  CommunicationTypeEnum,
  InquiryFields,
  RemoteDirectUploadLinkInput,
  SenderFields
} from '@/types/graphql/schemaTypes';

const CHAT_SUBMIT_CONFIG_KEY = 'enterSubmit';

@Component({
  apollo: {
    messages: {
      query: fetchChannelsWithInquiryQuery,
      variables() {
        return {
          inquiryArgs: {
            inquiryId: this.$route.params.id
          },
          channelId: this.channelId,
          pagination: {
            page: 1,
            per: 9999 // fetch all until we have a fetchmore
          }
        };
      },
      update(data) {
        this.inquiry = data && data.inquiries && data.inquiries[0];
        this.channel = this.inquiry.channelList[0];
        this.partnerName = this.channel?.partnerMembers?.[0]?.partner?.name;
        // tslint:disable-next-line max-line-length
        return this.channel.channelMessages.sort((a: ChannelMessageFields, b: ChannelMessageFields) => {
          return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
        });
      }
    }
  },
  components: {
    ChannelOverlay,
    Message,
    'el-button': Button,
    'el-checkbox': Checkbox,
    'el-input': Input,
    'el-form': Form,
    'el-form-item': FormItem
  }
})
export default class Channel extends Mixins(ScrollBottom) {
  @Prop({ required: true, type: String }) channelId!: string;
  @Prop({ default: false, type: Boolean }) embeded!: boolean;

  localStorageMessageKey = '';

  private channel: ChannelFields | null = null;
  private contentInput = '';
  private enterSubmit = sessionStorage.getItem(CHAT_SUBMIT_CONFIG_KEY) === 'true';
  private hasUnreadMessage = false;
  private inquiry: InquiryFields | null = null;
  private isLoading = false;
  private isUploading = false;
  private messages: ChannelMessageFields[] = [];
  private partnerName: string | null = null;

  // https://github.com/kaorun343/vue-property-decorator#-refrefkey-string-decorator
  @Ref('fileInput') readonly fileInput!: HTMLInputElement;
  @Ref('messagesBox') readonly messagesBox!: HTMLInputElement;
  @Ref('overlay') readonly overlay!: ChannelOverlay;

  created() {
    this.initContentInput(this.channelId);
  }

  mounted() {
    this.$root.$on('channelMessageCreated', this.appendChannelMessage);
  }

  destroyed() {
    this.$root.$off('channelMessageCreated', this.appendChannelMessage);
  }

  initContentInput(channelId: string) {
    this.localStorageMessageKey = this.genLocalStorageKey(this.channelId);
    this.contentInput = sessionStorage.getItem(this.localStorageMessageKey) || '';
  }

  genLocalStorageKey(channelId: string) {
    return `channel-${this.channelId}-message`;
  }

  private get title(): string {
    let typedName;
    switch (this.channelType) {
      case ChannelTypeEnum.private:
        typedName = this.$t('channel.title-private');
        break;
      case ChannelTypeEnum.partner:
        typedName = this.$t('channel.title');
        return `${typedName} - ${this.partnerName}`;
      case ChannelTypeEnum.public:
      default:
        typedName = this.$t('channel.title');
        break;
    }
    return `${typedName} - ${this.customerOrganizationName}`;
  }

  private get channelType(): ChannelTypeEnum {
    if (this.channel) {
      return this.channel.channelType;
    }
    return ChannelTypeEnum.public;
  }

  private get customerOrganizationName(): string | null {
    return this.inquiry?.customerOrganization?.name || null;
  }

  appendChannelMessage(channelMessage: ChannelMessageFields) {
    if (channelMessage.channel.id === this.channelId) {
      this.messages.push(channelMessage);
      this.scrollBottom(this.messagesBox);
      if (this.overlay?.isMinimized) {
        this.hasUnreadMessage = true;
      }
    }
  }

  getAvatarUrl(sender: SenderFields) {
    return sender.avatar && sender.avatar.url;
  }

  handleKey(event: KeyboardEvent) {
    // submit with enter // new line with shift
    if (this.enterSubmit && event.keyCode === 13 && !event.shiftKey) {
      event.preventDefault();
      this.submit();
    }
  }

  openFile() {
    this.fileInput.click();
  }

  saveText(event: KeyboardEvent) {
    if (!!this.localStorageMessageKey && event.keyCode !== 13) {
      sessionStorage.setItem(this.localStorageMessageKey, this.contentInput);
      return;
    }
    event.preventDefault();
  }

  saveEnterSubmit(value: boolean) {
    sessionStorage.setItem(CHAT_SUBMIT_CONFIG_KEY, value.toString());
  }

  scrollDown(isVisible: boolean) {
    if (isVisible && this.messagesBox) {
      this.scrollBottom(this.messagesBox);
    }
  }

  showAvatar(index: number, senderId: string) {
    if (!index) {
      return true;
    }
    return senderId !== this.messages[index - 1].sender.id;
  }

  async submit(file?: RemoteDirectUploadLinkInput) {
    if ((!file && this.isEmpty) || this.isLoading) {
      return;
    }

    try {
      this.isLoading = true;

      let ChannelMessageInput = {};
      if (file) {
        ChannelMessageInput = {
          attachment: { key: file.key },
          communicationType: CommunicationTypeEnum.file
        };
      } else {
        ChannelMessageInput = {
          communicationType: CommunicationTypeEnum.text,
          content: this.contentInput
        };
      }

      const response = await channelService.createChannelMessage(
        ChannelMessageInput,
        this.channelId
      );

      if (response && response.data) {
        sessionStorage.removeItem(this.localStorageMessageKey);
        this.contentInput = '';
      }
    } catch (error) {
      this.$store.dispatch('Alert/addAlert', {
        type: 'error',
        message: this.$t('error.push'),
        time: 2000
      });
    } finally {
      this.isLoading = false;
    }
  }

  async uploadFile(event: Event) {
    const files = this.fileInput.files || [];

    this.isUploading = true;
    for (let i = 0; i < files.length; i += 1) {
      const file = files[i];

      try {
        const response = await signedUploadService.startClientUpload(file);

        if (response) {
          this.submit(response);
        }
      } catch (error) {
        this.$store.dispatch('Alert/addAlert', {
          type: 'error',
          message: this.$t('error.push'),
          time: 2000
        });
      }
    }
    this.isUploading = false;
  }

  get channelClass() {
    switch (this.channel.channelType) {
      case 'private':
        return 'bg-yellow-100';
      case 'partner':
        return 'bg-blue-100';
    }
  }

  get isEmpty() {
    return this.contentInput.trim() === '';
  }

  @Watch('channelId')
  onSwitchChannel(channelId: string) {
    this.scrollDown(true);
    // re init localstorag key to properly store draft message (even when switching channel)
    this.initContentInput(this.channelId);
  }
}
